import { useMutation, useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as Sentry from '@sentry/core';
import produce from 'immer';
import PQueue from 'p-queue';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ConfirmationModal } from '@oui/app-core/src/components/ConfirmationModal';
import { ErrorPresenter } from '@oui/app-core/src/components/ErrorPresenter';
import { HeaderButtons, HeaderItem } from '@oui/app-core/src/components/HeaderButtons';
import { ScrollView } from '@oui/app-core/src/components/ScrollView';
import { Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { Theme, useTheme } from '@oui/app-core/src/styles';
import { NumberParam } from '@oui/app-core/src/types/navigation';
import { graphql, VariablesOf } from '@oui/lib/src/graphql/tada';

import { useWordPairingContext } from '../../components';
import { RatingAfterForm } from '../../components/RatingAfterForm/RatingAfterForm';
import { WordPairingBreak } from '../../components/WordPairingBreak/WordPairingBreak';
import {
  WordPairingResult,
  WordPairingResultFragment,
} from '../../components/WordPairingResult/WordPairingResult';
import { WordPairingRound } from '../../components/WordPairingRound/WordPairingRound';
import { StackScreenProps } from '../../types/navigation';

const WordPairingPracticeFragment = graphql(
  `
    fragment WordPairingPractice on WordPairingPractice @_unmask {
      practiceID
      practiceValues {
        ratings {
          type
          value
        }
      }
      wordPairing {
        isComplete
        numRounds
        rounds {
          numQuestions
          numAnswers
          numCorrectAnswers
          time
          ...WordPairingResult
        }
      }
    }
  `,
  [WordPairingResultFragment],
);

export const WordPairingNewQuery = graphql(
  `
    query WordPairingNew($practiceID: UUID!, $hasPracticeID: Boolean!) {
      user {
        ID
        role {
          ID
          wordPairingWidget {
            ID
            numQuestionsPerRound
            wordPairingByID(practiceID: $practiceID) @include(if: $hasPracticeID) {
              ...WordPairingPractice
            }
          }
        }
      }
    }
  `,
  [WordPairingPracticeFragment],
);

export const WordPairingNewMutation = graphql(
  `
    mutation WordPairingNewUpdate($input: MutationUpdateWordPairingPracticeInput!) {
      updateWordPairingPractice(input: $input) {
        ...WordPairingPractice
      }
    }
  `,
  [WordPairingPracticeFragment],
);

export const WordPairingNewCreateMutation = graphql(`
  mutation WordPairingNewCreate($input: MutationAddWordPairingPracticeInput!) {
    addWordPairingPractice(input: $input) {
      practiceID
    }
  }
`);

function getBackgroundColor(theme: Theme, step: 'finish' | 'break' | 'results' | 'round') {
  return {
    break: theme.color.gray100,
    round: undefined,
    finish: undefined,
    results: theme.color.dark,
  }[step];
}

/**
 * Flow to create / perform a new word pairing exercise.
 */
export const WordPairingNewInner = () => {
  const wordPairingContext = useWordPairingContext();
  const route = useRoute<StackScreenProps<'WordPairingNew'>['route']>();
  const navigation = useNavigation<StackScreenProps<'WordPairingNew'>['navigation']>();
  const { theme } = useTheme();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const roundNum = Number(route.params.round);
  const [roundFinished, setRoundFinished] = useState(false);
  const { data } = useQuery(WordPairingNewQuery, {
    variables: {
      // practiceID is guaranteed to be present in the Inner component
      practiceID: route.params.practiceID!,
      hasPracticeID: true,
    },
  });
  const updateQueue = useRef<PQueue>(undefined);
  if (!updateQueue.current) updateQueue.current = new PQueue({ concurrency: 1 });
  const [update] = useMutation(WordPairingNewMutation);
  const answers = useRef<boolean[]>([]);

  const patientID = data?.user?.role?.ID;
  const practice = data?.user?.role?.wordPairingWidget?.wordPairingByID;

  const baseInput: VariablesOf<typeof WordPairingNewMutation>['input'] = {
    practiceID: route.params.practiceID!,
    practiceValues: {
      patientID: patientID!,
      ratings:
        practice?.practiceValues.ratings.map((r) => ({
          type: r.type,
          value: r.value,
        })) ?? [],
    },
    wordPairing: {
      rounds:
        practice?.wordPairing.rounds.map((r) => ({
          numAnswers: r.numAnswers,
          numCorrectAnswers: r.numCorrectAnswers,
          time: r.time,
        })) ?? [],
    },
  };

  const step: 'finish' | 'break' | 'results' | 'round' =
    route.params.break === 'true'
      ? route.params.round === '2'
        ? 'finish'
        : 'break'
      : roundFinished
        ? 'results'
        : 'round';

  useLayoutEffect(() => {
    navigation.setOptions({
      headerStyle: {
        backgroundColor: getBackgroundColor(theme, step),
        borderWidth: 0,
      },
      headerTitle: '',
      headerLeft: ({ testID }) => (
        <HeaderButtons left>
          <HeaderItem
            aria-label="Cancel exercise"
            title="Cancel exercise"
            iconName="arrow-left"
            color={theme.color.primary100}
            onPress={navigation.goBack}
            testID={testID}
          />
        </HeaderButtons>
      ),
      headerRight:
        step === 'results' || step === 'break'
          ? () => {
              return (
                <HeaderButtons>
                  <Text
                    text={`${step === 'results' ? 'Result' : step === 'break' ? 'Break' : ''}`}
                    size={15}
                    color={'white'}
                  />
                </HeaderButtons>
              );
            }
          : undefined,
    });
  }, [step, roundNum, navigation, theme]);

  useEffect(() => {
    return navigation.addListener('beforeRemove', (e) => {
      if (step === 'finish' || showCancelModal) {
        return;
      }

      e.preventDefault();
      setShowCancelModal(true);
    });
  }, [navigation, step, showCancelModal]);

  if (!practice) return null;
  const numPairs =
    wordPairingContext.numQuestionsPerRoundOverride ??
    practice.wordPairing.rounds[0]?.numQuestions ??
    data.user?.role?.wordPairingWidget?.numQuestionsPerRound;
  return (
    <ScrollView
      testID="WordPairingNew"
      style={{ flex: 1 }}
      scrollEnabled={step !== 'round'}
      topOverflowColor={getBackgroundColor(theme, step)}
      bottomOverflowColor={getBackgroundColor(theme, step)}
      contentContainerStyle={{
        padding: 20,
        backgroundColor: getBackgroundColor(theme, step),
        flexGrow: 1,
      }}
    >
      {step === 'finish' ? (
        <RatingAfterForm
          numRounds={practice?.wordPairing.numRounds}
          onSubmit={(value) => {
            update({
              variables: {
                input: produce(baseInput, (draft) => {
                  draft.practiceValues.ratings = [{ type: 'RATING_AFTER', value }];
                }),
              },
            })
              .then(() => {
                navigation.replace('WordPairingShow', {
                  practiceID: route.params.practiceID!,
                });
              })
              .catch(Sentry.captureException);
          }}
        />
      ) : step === 'break' ? (
        <WordPairingBreak
          round={roundNum}
          onStart={() => {
            setRoundFinished(false);
            navigation.setParams({
              round: (roundNum + 1).toString() as NumberParam,
              break: 'false',
            });
          }}
        />
      ) : step === 'results' && practice ? (
        <WordPairingResult
          round={roundNum}
          data={practice.wordPairing.rounds[roundNum]}
          onNext={() => {
            answers.current = [];
            navigation.setParams({ break: 'true' });
          }}
        />
      ) : (
        <WordPairingRound
          numPairs={numPairs}
          parentPadding={20}
          onAnswer={async ({ index, correct, timeMs }) => {
            answers.current.push(correct);

            updateQueue.current!.clear();
            // the promise returned here is the enqueued promise which we don't want to wait for
            void updateQueue.current!.add(() =>
              update({
                variables: {
                  input: produce(baseInput, (draft) => {
                    draft.wordPairing.rounds[roundNum] = {
                      numAnswers: answers.current.length,
                      numCorrectAnswers: answers.current.filter((a) => a).length,
                      time: timeMs,
                    };
                  }),
                },
              }),
            );

            const finished = index === numPairs - 1;
            if (finished) {
              // wait for animation to complete in WordPairingRound
              await new Promise((res) => setTimeout(res, 1000));
              await updateQueue.current!.onIdle();
              setRoundFinished(finished);
            }
          }}
        />
      )}
      <ConfirmationModal
        visible={showCancelModal}
        title="Leave before finishing?"
        description={`You are about the leave this exercise before completing all ${practice.wordPairing.numRounds}. Once you exit, you’ll have to start over.`}
        cancelText="Yes, leave"
        confirmText="Let's finish"
        onCancel={() => navigation.navigate('WordPairingIndex', {})}
        onConfirm={() => setShowCancelModal(false)}
      />
    </ScrollView>
  );
};

export function WordPairingNew() {
  const route = useRoute<StackScreenProps<'WordPairingNew'>['route']>();
  const navigation = useNavigation<StackScreenProps<'WordPairingNew'>['navigation']>();
  const { data } = useQuery(WordPairingNewQuery, {
    variables: {
      practiceID: '00000000-0000-0000-0000-000000000000', // we need a valid UUID even if it's not used in the query
      hasPracticeID: false,
    },
    skip: !!route.params.practiceID,
  });
  const [create, { error }] = useMutation(WordPairingNewCreateMutation);

  const patientID = data?.user?.role?.ID;
  const setParams = navigation.setParams;
  useEffect(() => {
    if (!route.params.practiceID && patientID) {
      void create({
        variables: {
          input: { practiceValues: { patientID, ratings: [] }, wordPairing: { rounds: [] } },
        },
      }).then((res) => {
        if (res.data?.addWordPairingPractice.practiceID) {
          setParams({ practiceID: res.data.addWordPairingPractice.practiceID });
        }
      });
    }
  }, [route.params.practiceID, create, patientID, setParams]);

  return route.params.practiceID ? (
    <WordPairingNewInner />
  ) : error ? (
    <View>
      <ErrorPresenter error={error} />
    </View>
  ) : null;
}
